import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, Youtube, Mail, MapPin, Clock, ArrowUpRight } from 'lucide-react';

const SOCIALS = [
  { Icon: Instagram, href: 'https://www.instagram.com/forrealrutviii', label: 'Instagram', handle: '@forrealrutviii' },
  { Icon: Youtube, href: 'https://youtube.com/@forrealrutvii', label: 'YouTube', handle: '@forrealrutvii' },
];

const QUICK_LINKS = [
  { label: 'Services', href: '#services' },
  { label: 'Process', href: '#process' },
  { label: 'Social', href: '#social' },
  { label: 'About', href: '#about' },
  { label: 'Blog', href: '#blog' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      data-testid="footer"
      className="relative bg-[#1C1917] text-[#F4F4F3] pt-20 sm:pt-28 pb-10 px-6 sm:px-8 lg:px-12 overflow-hidden"
    >
      {/* Ambient lavender glow */}
      <motion.div
        aria-hidden="true"
        animate={{ scale: [1, 1.1, 1], opacity: [0.35, 0.55, 0.35] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        className="pointer-events-none absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-[#AA94FF]/25 blur-3xl"
      />
      <motion.div
        aria-hidden="true"
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="pointer-events-none absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-[#FFA8F2]/20 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto">
        {/* Big brand wordmark */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16 sm:mb-20"
        >
          <p
            className="text-[18vw] lg:text-[13rem] font-black tracking-[-0.06em] leading-[0.85] text-[#F4F4F3]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Hype
            <span className="italic font-light text-[#AA94FF]">Lab</span>
            <span className="text-[#FFA952]">.</span>
          </p>
        </motion.div>

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 sm:gap-12 pb-12 border-b border-[#F4F4F3]/10">
          {/* Brand copy + socials */}
          <div className="md:col-span-5">
            <p className="text-base sm:text-lg text-[#F4F4F3]/75 leading-relaxed max-w-md mb-8">
              Content that grows your brand. UGC · short-form video · social strategy — built for scroll-stopping, not guesswork.
            </p>

            <div className="flex flex-col gap-3">
              {SOCIALS.map((s, i) => (
                <motion.a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  data-cursor-label="Follow"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
                  whileHover={{ x: 6 }}
                  className="group inline-flex items-center gap-3 text-[#F4F4F3]/80 hover:text-[#AA94FF] transition-colors max-w-fit"
                >
                  <span className="w-10 h-10 rounded-full border border-[#F4F4F3]/20 flex items-center justify-center group-hover:bg-[#AA94FF] group-hover:border-[#AA94FF] group-hover:text-[#1C1917] transition-all">
                    <s.Icon className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-semibold uppercase tracking-[0.15em]">
                    {s.handle}
                  </span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#AA94FF] mb-5">
              Navigate
            </p>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}>
                  <button
                    onClick={() => scrollTo(l.href)}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    data-cursor-label="Jump"
                    className="group inline-flex items-center gap-2 text-[#F4F4F3]/75 hover:text-[#F4F4F3] transition-colors text-base font-medium"
                  >
                    <span className="w-0 h-px bg-[#AA94FF] group-hover:w-4 transition-all duration-300" />
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact info */}
          <div className="md:col-span-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#AA94FF] mb-5">
              Get in touch
            </p>
            <ul className="space-y-4 text-[#F4F4F3]/80">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#AA94FF] mt-1 shrink-0" />
                <a
                  href="mailto:hello@hypelabmarketing.co.uk"
                  data-testid="footer-email"
                  className="hover:text-[#AA94FF] transition-colors text-sm sm:text-base"
                >
                  hello@hypelabmarketing.co.uk
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#AA94FF] mt-1 shrink-0" />
                <span className="text-sm sm:text-base">London, United Kingdom</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#AA94FF] mt-1 shrink-0" />
                <span className="text-sm sm:text-base">Mon – Fri · 9am–6pm GMT</span>
              </li>
            </ul>

            {/* Availability pill */}
            <div className="mt-6 inline-flex items-center gap-2 bg-[#F4F4F3]/5 border border-[#F4F4F3]/10 rounded-full px-3 py-1.5">
              <span className="relative flex w-2 h-2">
                <motion.span
                  animate={{ scale: [1, 2.5, 1], opacity: [0.6, 0, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-full bg-[#AA94FF]"
                />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#AA94FF]" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]">
                Taking 2 brands · Q2
              </span>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#F4F4F3]/50 text-sm">
            © {currentYear} HypeLab Marketing · Rutvi Ahir · All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-[#F4F4F3]/50">
            <a
              href="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-[#AA94FF] transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              data-testid="footer-terms-link"
              className="hover:text-[#AA94FF] transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
